import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types';
import { InfoCircleOutlined } from '@ant-design/icons';
import { message, Tooltip } from 'antd';
import { getScoringDetails } from '../../../Api/Services/CommonApiService';
import { getLeadScore } from '../../../Api/Services/LeadApiServices';
import { FetchAccountDetails } from '../../../Api/Services/AccountApiService';
import { FetchDealDetails } from '../../../Api/Services/DealApiService';
import { getContactScore } from '../../../Api/Services/ContactApiService';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
@keyframes sg-fade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
.sg-card{font-family:'Noto Sans', sans-serif;box-shadow: 0 1px 10px #00000026;margin-top: 10px; border: 0.1px solid #cdcdcd;background:#fff;border-radius:5px;overflow:hidden;animation:sg-fade .35s cubic-bezier(.22,.68,0,1.1) both;transition:box-shadow .2s;container-type:inline-size}
.sg-card,.sg-card *{box-sizing:border-box}
.sg-card:hover{box-shadow:0 8px 24px rgba(0,0,0,.10)}
.sg-body{display:flex;flex-direction:column;align-items:stretch;flex-wrap:nowrap}
.sg-gauge{flex:0 0 auto;min-width:0;padding:0px 8px 6px;position:relative}
.sg-gauge svg{max-width:240px;margin:0 auto}
.sg-side{flex:0 0 auto;min-width:0;border-top:1px solid #F0F0F0;background:#FAFAFA;padding:12px;display:flex;flex-direction:row;flex-wrap:nowrap;align-items:flex-start;justify-content:space-between;gap:12px}
.sg-col{display:flex;flex-direction:column;gap:8px;min-width:0}
.sg-label{font-size:9px;font-weight:700;color:#9CA3AF;text-transform:uppercase;letter-spacing:.1em}
.sg-pill{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;font-size:13px;font-weight:700}
.sg-dot{width:8px;height:8px;border-radius:50%}
.sg-nums{display:flex;align-items:baseline;gap:4px;font-variant-numeric:tabular-nums;font-size:12px;font-weight:700;color:#9CA3AF}
.sg-nums b{font-size:20px;font-weight:800;line-height:1}
.sg-trend{display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:700}
.sg-trend span{font-weight:500;color:#9CA3AF}
@keyframes sg-sweep{
0%{stroke-dashoffset:28;stroke:#00B14F}
30%{stroke:#00B14F}
45%{stroke:#F59E0B}
55%{stroke:#F59E0B}
70%{stroke:#EF4444}
100%{stroke-dashoffset:-100;stroke:#EF4444}}
@keyframes sg-shimmer{0%{background-position:-200px 0}100%{background-position:200px 0}}
@keyframes sg-pulse{0%,100%{opacity:.55}50%{opacity:1}}
.sg-sweep{animation:sg-sweep .6s linear infinite}
.sg-bone{border-radius:6px;background:linear-gradient(90deg,#F0F0F0 0,#F8F8F8 50%,#F0F0F0 100%);background-size:400px 100%;animation:sg-shimmer 1.4s linear infinite}
.sg-loadtxt{animation:sg-pulse 1.4s ease-in-out infinite}
@media (prefers-reduced-motion:reduce){.sg-sweep,.sg-bone,.sg-loadtxt{animation:none}}
.sg-empty{padding:28px 16px;text-align:center;color:#9CA3AF;font-size:12px}
`;
let injected = false;
function injectCSS() {
    if (injected || typeof document === 'undefined') return;
    const el = document.createElement('style');
    el.textContent = CSS;
    document.head.appendChild(el);
    injected = true;
}

const STATUS = {
    hot: { color: '#EF4444', soft: 'rgba(239,68,68,.10)' },
    warm: { color: '#F59E0B', soft: 'rgba(245,158,11,.12)' },
    cold: { color: '#00B14F', soft: 'rgba(0,177,79,.10)' },
    none: { color: '#9CA3AF', soft: 'rgba(156,163,175,.14)' },
};
const THRESHOLD_COLOR = '#3B82F6';

function statusKey(state) {
    const s = (state || '').toLowerCase();
    if (s.startsWith('hot')) return 'hot';
    if (s.startsWith('warm')) return 'warm';
    if (s.startsWith('cold')) return 'cold';
    return 'none';
}

const toSafeNumber = (val) => {
    const num = parseFloat(val);
    return Number.isNaN(num) ? 0 : num;
};
const isValid = (v) => Boolean(v) && v !== 'NaN';

function getScoreChangeInfo(currentScore, lastWeekScore) {
    const current = toSafeNumber(currentScore);
    const last = toSafeNumber(lastWeekScore);
    let percentageChange = 0;
    let isIncrease = false;
    if (last === 0 && current === 0) {
        percentageChange = 0;
    } else if (last === 0) {
        percentageChange = 100;
        isIncrease = true;
    } else {
        const diff = current - last;
        percentageChange = (diff / last) * 100;
        isIncrease = diff > 0;
    }
    return { percentageChange: Number(percentageChange.toFixed(2)), isIncrease };
}

function buildModel(response, previous, scoreKey, thresholdFromMax) {
    const dataScore = response?.[scoreKey];
    const highScore = response?.higerScore;
    const thresholdScore = thresholdFromMax ? response?.higerScore : response?.thresholdScore;
    const thresholdState = response?.thresholdState;

    const okT = isValid(thresholdScore);
    const okMax = isValid(highScore);
    const okScore = isValid(dataScore);
    const okState = isValid(thresholdState);

    const max = okMax ? Number(highScore) : 0;
    const threshold = okT ? Number(thresholdScore) : max;
    const score = okScore ? Number(dataScore) : 0;

    let state = 'N/A';
    if ((okT || okMax) && okScore) {
        state = threshold <= score ? 'Hot' : okState ? thresholdState : 'Hot';
    }
    return {
        hasScore: dataScore !== undefined && dataScore !== null && dataScore !== '' && dataScore !== 'NaN',
        score,
        max,
        threshold,
        state,
        change: getScoreChangeInfo(score, previous?.score),
    };
}

const SOURCES = {
    '?LEAD': { type: 'Lead', scoreKey: 'leadScore', fetch: (id) => getLeadScore(id) },
    '?DEAL': { type: 'Deal', scoreKey: 'dealScore', fetch: async (id) => (await FetchDealDetails(id)).data },
    '?CONTACT': { type: 'Contact', scoreKey: 'contactScore', fetch: (id) => getContactScore(id) },
    '?ACCOUNT': {
        type: 'Account',
        scoreKey: 'accountScore',
        thresholdFromMax: true,
        fetch: async (id) => (await FetchAccountDetails(id)).data,
    },
};

const CX = 100;
const CY = 100;
const R = 76;
const pt = (f, r = R) => [CX - r * Math.cos(Math.PI * f), CY - r * Math.sin(Math.PI * f)];
function arcPath(f0, f1) {
    const [x0, y0] = pt(f0);
    const [x1, y1] = pt(f1);
    return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}
const ZONES = [
    { f0: 0.005, f1: 0.328, color: STATUS.cold.color },
    { f0: 0.338, f1: 0.662, color: STATUS.warm.color },
    { f0: 0.672, f1: 0.995, color: STATUS.hot.color },
];

function useCountUp(target, ms = 900) {
    const [v, setV] = useState(0);
    useEffect(() => {
        const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (reduce) { setV(target); return undefined; }
        let raf;
        let start;
        const step = (t) => {
            if (start === undefined) start = t;
            const p = Math.min((t - start) / ms, 1);
            setV(target * (1 - (1 - p) ** 3));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, ms]);
    return v;
}

function GaugeSkeleton() {
    const len = 100;
    return (
        <div className="sg-card" aria-busy="true" aria-live="polite">
            <div className="sg-body">
                <div className="sg-gauge">
                    <svg viewBox="0 0 200 122" style={{ width: '100%', display: 'block' }}>
                        {ZONES.map((z) => (
                            <path key={z.color} d={arcPath(z.f0, z.f1)} fill="none" stroke={z.color}
                                strokeOpacity=".12" strokeWidth="14" strokeLinecap="round" />
                        ))}
                        <path d={arcPath(0.001, 0.999)} pathLength={len} fill="none" stroke="#00B14F" strokeWidth="14"
                            strokeLinecap="round" strokeDasharray={`28 ${len}`} className="sg-sweep" />
                        <rect x="68" y="72" width="64" height="20" rx="6" fill="#F0F0F0" />
                    </svg>
                </div>
                <div className="sg-side">
                    <div className="sg-col">
                        <div className="sg-bone" style={{ width: 90, height: 18 }} />
                        <div className="sg-bone" style={{ width: 90, height: 12 }} />
                    </div>
                    <div className="sg-col">
                        <div className="sg-bone" style={{ width: 80, height: 22, borderRadius: 999 }} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function Gauge({ score, max, threshold, color }) {
    const progress = useCountUp(score);
    const shown = progress;
    const f = max > 0 ? Math.min(Math.max(shown / max, 0), 1) : 0;
    const t = max > 0 ? Math.min(Math.max(threshold / max, 0), 1) : 1;
    const [ex, ey] = pt(Math.max(f, 0.001));
    const [tx0, ty0] = pt(t, R - 14);
    const [tx1, ty1] = pt(t, R + 14);
    const len = Math.PI * R;
    return (
        <svg viewBox="0 0 200 122" style={{ width: '100%', display: 'block' }} role="img"
            aria-label={`Score ${score.toFixed(2)} of ${max}`}>
            {ZONES.map((z) => (
                <path key={z.color} d={arcPath(z.f0, z.f1)} fill="none" stroke={z.color}
                    strokeOpacity=".16" strokeWidth="14" strokeLinecap="round" />
            ))}
            <path d={arcPath(0.001, 0.999)} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round"
                strokeDasharray={len} strokeDashoffset={len * (1 - f)} />
            <line x1={tx0} y1={ty0} x2={tx1} y2={ty1} stroke={THRESHOLD_COLOR} strokeWidth="2.5" strokeLinecap="round" />
            <circle cx={ex} cy={ey} r="7" fill="#fff" stroke={color} strokeWidth="3.5" />
            <text x={CX} y={CY - 8} textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="30" fontWeight="800" fill="#111">
                {shown.toFixed(2)}
            </text>
            <text x={CX} y={CY + 8} textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="9" fontWeight="600" fill="#9CA3AF">
                SCORE
            </text>
            <text x={pt(0, R)[0]} y={CY + 22} textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="9" fontWeight="600" fill="#9CA3AF">0</text>
            <text x={pt(1, R)[0]} y={CY + 22} textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="9" fontWeight="700" fill="#6B7280">{Math.round(max)}</text>
        </svg>
    );
}

Gauge.propTypes = {
    score: PropTypes.any.isRequired,
    max: PropTypes.any.isRequired,
    threshold: PropTypes.any.isRequired,
    color: PropTypes.any.isRequired,
};

function Trend({ change }) {
    const pct = Math.abs(change?.percentageChange || 0);
    const flat = pct === 0;
    const up = change?.isIncrease;
    const color = flat ? '#9CA3AF' : up ? '#00B14F' : '#EF4444';
    return (
        <div className="sg-trend" style={{ color }}>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {flat ? <line x1="3" y1="8" x2="13" y2="8" />
                    : up ? <><line x1="8" y1="13" x2="8" y2="3" /><polyline points="3.5,7.5 8,3 12.5,7.5" /></>
                        : <><line x1="8" y1="3" x2="8" y2="13" /><polyline points="3.5,8.5 8,13 12.5,8.5" /></>}
            </svg>
            {pct.toFixed(0)}%<span>since last week</span>
        </div>
    );
}

Trend.propTypes = {
    change: PropTypes.any.isRequired,
};

const MyGauge = ({ UrlId, trigger }) => {
    const [model, setModel] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { injectCSS(); }, []);

    useEffect(() => {
        const src = UrlId ? SOURCES[UrlId[0]] : undefined;
        if (trigger === '') return undefined;
        if (!src) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const [response, previous] = await Promise.all([
                    src.fetch(UrlId[1]),
                    getScoringDetails(src.type, UrlId[1]),
                ]);
                if (!cancelled) setModel(buildModel(response, previous, src.scoreKey, src.thresholdFromMax));
            } catch (error) {
                if (!cancelled) message.error(typeof error === 'string' ? error : error?.message || 'Unable to load score');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [UrlId, trigger]);

    if (loading && !model) {
        return <GaugeSkeleton />;
    }
    if (!model || !model.hasScore) {
        return <div className="sg-empty">No score yet</div>;
    }

    const key = statusKey(model.state);
    const { color, soft } = STATUS[key];
    const label = key === 'none' ? 'Undefined' : model.state;

    return (
        <div className="sg-card">
            <div className="sg-body">
                <div className="sg-gauge">
                    <Gauge score={model.score} max={model.max} threshold={model.threshold} color={color} />
                </div>
                <div className="sg-side">
                    <div className="sg-col">
                        <div>
                            <div className="sg-label" style={{ marginBottom: 4 }}>
                                Score / Threshold{' '}
                                <Tooltip title="Predictive Conversion Threshold">
                                    <InfoCircleOutlined style={{ fontSize: 10, cursor: 'pointer' }} />
                                </Tooltip>
                            </div>
                            <div className="sg-nums">
                                <b style={{ color }}>{model.score.toFixed(2)}</b>/
                                <span style={{ color: THRESHOLD_COLOR }}>{model.threshold.toFixed(2)}</span>
                            </div>
                        </div>
                        <Trend change={model.change} />
                    </div>
                    <div className="sg-col">
                        <div className="sg-label">Status</div>
                        <span className="sg-pill" style={{ background: soft, color }}>
                            <span className="sg-dot" style={{ background: color }} />{label}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

MyGauge.propTypes = {
    UrlId: PropTypes.array.isRequired,
    trigger: PropTypes.string.isRequired,
};

export default React.memo(MyGauge);